import React, { useState, useEffect, memo, useMemo, useCallback } from 'react';
import moment from 'moment';
import cx from 'classnames';
import { ExclamationCircleFilled, MessageOutlined, RollbackOutlined } from '@ant-design/icons';
import { Button, message, Tooltip } from 'antd';
import { useHistory, useLocation } from 'react-router-dom';
import { throttle } from 'lodash';

import { useFunction, useConvSetting } from '@laigu/store';
import MyIcon from '../my-icon';
import { TrusteeshipTag } from './components/trusteeship-tag';

import { TRANSLATE_DATA } from 'chat/constants/index';

import style from './index.less';

function Translation({ translateData, isAgent }) {
  const { targetLanguage, sourceLanguage, fail, translated, isSameLanguage } = translateData;
  if (fail) {
    return <div className={style['extreme-small']}>{'翻译失败'}</div>;
  }
  if (translated && !isSameLanguage) {
    return (
      <div className={cx(style['extreme-small'], isAgent && style.agent_trans)}>
        {`翻译：${String(TRANSLATE_DATA[sourceLanguage] ?? '')} → ${String(TRANSLATE_DATA[targetLanguage] ?? '')}`}
      </div>
    );
  }
  return <></>;
}

const SensitiveInfo = () => {
  return (
    <div className={style['extra-info-wrapper']}>
      <ExclamationCircleFilled />
      <span>{'该消息包含违规内容，已被屏蔽'}</span>
    </div>
  );
};

const FailSend = ({ clickFailIcon, errorMsg, type }) => {
  return (
    <span className={style['extra-info-wrapper']}>
      <ExclamationCircleFilled />
      {type === 'image' ? (
        <span>
          {'发送失败'}
          {errorMsg ? '，' + errorMsg : ''}
        </span>
      ) : (
        <span>{'该消息发送失败'}</span>
      )}
    </span>
  );
};

const MessageInfo = memo(({
  render,
  data,
  clickFailIcon = () => {},
  handleWithdrawMessage,
  handleRemindMessage,
  handleTranslateMessage,
  handleSpeechToTxt,
  handleIntelligentRecommend
}) => {
  const {
    id,
    agentName,
    time,
    fromType,
    fail,
    content = [],
    extra,
    msgWithdraw = {},
    customExtra = {},
    read_status,
    action,
    msgType,
    contentType,
    translateEnabled,
    translateLanguage,
    queryTranslateQuota,
    translate,
    speechToTxt,
    trusted,
    convBelongToMe,
    isLastStream = false,
    source
  } = data;
  const { pathname } = useLocation();
  const history = useHistory();
  const { convSetting } = useConvSetting();

  const isAgent = fromType === 'agent' || fromType === 'bot';
  const [hoverStatus, setHoverStatus] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const convTranslationAvailable = useFunction('conv_translation');
  const speechToTxtAvailable = useFunction('conversation_message_asr');
  const trusteeshipPermission = useFunction('robot_trusteeship');
  // 智能回复
  const intelligentRecommendAvailable = useFunction('conv_intelligent_recommend');
  const isTrusteeshipConv = trusted === 1;
  const failStatus = fail && isAgent;
  const { withdrawEnable, timeout, is_truested = false } = msgWithdraw;
  // 是否包含敏感内容
  const containsSensitiveWords = extra && extra.contains_sensitive_words;
  // 是否能引用
  const canRemind = customExtra && customExtra.canRemind && !failStatus;

  const canWithDraw = showWithdraw && (!extra || !extra.is_withdrawn) && !failStatus;
  // 是否含有操作
  const has_operator_msg = data?.extra?.operator_msg?.length;
  const translatable =
    convTranslationAvailable &&
    contentType === 'text' &&
    !isAgent &&
    (!translate || translate?.fail) &&
    translateEnabled;

  const { showType = [] } = convSetting?.rightRelateSetting || {};

  const showIntelligentRecommend = !!showType.find(val => val === 'intelligentRecommend');

  // 是否含有智能回复
  const msgIntelligentrecommendAvailable =
    showIntelligentRecommend && intelligentRecommendAvailable && contentType === 'text' && !isAgent;

  const speechToTxtAble = contentType === 'audio' && !isAgent && (!speechToTxt || speechToTxt?.fail);
  useEffect(() => {
    const msgTime =
      moment
        .utc(time)
        .local()
        .toDate()
        .getTime() || 0;
    const range = (Date.now() - msgTime) / 1000;

    setShowWithdraw(withdrawEnable && hoverStatus && range < timeout);
  }, [hoverStatus, withdrawEnable]);
  const showOperate = hoverStatus && (canRemind || canWithDraw || translatable || speechToTxtAble);

  const toTranslate = useCallback(
    msg => {
      const quota = queryTranslateQuota();
      if (!quota) {
        return;
      }
      const reqData = {
        source_lang: '',
        target_lang: translateLanguage || 'zh',
        texts: [
          {
            msg_id: msg.id,
            source_text: msg.content[0].body
          }
        ]
      };
      handleTranslateMessage(reqData, msg.track_id).catch(e => {
        // message.error(e.message);
      });
    },
    [handleTranslateMessage, translateLanguage, queryTranslateQuota]
  );

  const toSpeechToTxt = useCallback(
    msg => {
      handleSpeechToTxt(msg.id).catch(e => {
        if (e?.data?.message) {
          message.error(e?.data?.message);
        }
      });
    },
    [handleSpeechToTxt]
  );

  const goMessageSetting = useCallback(() => {
    history.push('/setting/app-livechat/setting-auto-message/web');
  }, []);

  const goAutoRule = useCallback(() => {
    history.push('/setting/auto-rule/web');
  }, []);

  const onFireFoldSetting = useCallback(() => {
    window.dispatchEvent(new Event('onStreamFoldSetting'));
  }, []);

  const getPopupContainer = useCallback(e => {
    return e.parentNode;
  }, []);

  const toIntelligentRecommend = useCallback(
    throttle(msg => {
      const content = msg?.content[0]?.body;

      if (content) {
        handleIntelligentRecommend(content);
      }
    }, 2500),
    [handleIntelligentRecommend]
  );

  const tag = useMemo(() => {
    if (extra && extra.from_outside && fromType === 'agent' && msgType === 'system' && action === 'message') {
      let text = '外部系统消息';
      if (source === 'redbook') {
        text = '小红书' + '-' + '平台消息';
      }
      if (source === 'douyin') {
        text = '抖音' + '-' + '平台消息';
      }
      if (source === 'wx_video') {
        text = '微信视频号' + '-' + '平台消息';
      }
      return (
        <li className={style.outside_info_tag}>
          <span>{text}</span>
        </li>
      );
    }
    if (isAgent && pathname.startsWith('/chat')) {
      let text = '';
      let goToPage = goMessageSetting;
      if (msgType === 'welcome' && extra?.welcome_msg_type === 'enterprise' && action === 'message') {
        text = '企业欢迎语';
      }
      if (msgType === 'welcome' && extra?.welcome_msg_type === 'agent' && action === 'message') {
        text = '个人欢迎语';
      }
      if (msgType === 'welcome' && extra?.event_rule_id && action === 'message') {
        text = '自动规则';
        goToPage = goAutoRule;
      }
      if (msgType === 'promotion' && action === 'message') {
        text = '推广消息';
      }
      if (msgType === 'guide' && action === 'message') {
        text = '无消息访客引导';
      }
      if (msgType === 'auto_reply' && action === 'message') {
        text = '自动消息';
      }
      if (msgType === 'PRIVATE_KEYWORD_REPLY' && action === 'message') {
        text = '关键词回复';
      }
      if (msgType === 'COMMENT_FOLLOW_FOLLOWERS' && action === 'message') {
        text = '评论追粉';
      }
      if (msgType === 'AUTO_FOLLOW_FOLLOWERS' && action === 'message') {
        text = '自动追粉';
      }
      if (msgType === 'BATCH_SEND_PLAN' && action === 'message') {
        text = '批量发送计划';
      }
      return (
        <Tooltip
          placement='topRight'
          overlayClassName={style.tooltip}
          title={
            <div>
              <span>{'这是一条系统设置的自动消息'}</span>
              <Button type='link' onClick={onFireFoldSetting}>
                {'隐藏设置'}
              </Button>
              <Button type='link' onClick={goToPage}>
                {'消息设置'}
              </Button>
            </div>
          }
        >
          <li className={style.info_tag}>
            <span>{text}</span>
          </li>
        </Tooltip>
      );
    }
    return null;
  }, [isAgent, action, msgType, contentType, onFireFoldSetting, pathname]);

  return (
    <div
      className={`${style['info-wrapper']} ${isLastStream && !has_operator_msg ? style['last-stream'] : ''} ${
        !isAgent ? '' : style['info-wrapper-right']
      }`}
      onMouseLeave={() => setHoverStatus(false)}
    >
      <ul className={style.info}>
        <li>
          {customExtra && customExtra.getMsgStatusType === 'read' ? (
            read_status === 3 ? (
              <Tooltip getPopupContainer={getPopupContainer} title={'已读'}>
                <MyIcon className={style['read-status']} type='ecoboost-iconfontic_chat_read' title={'已读'} />
              </Tooltip>
            ) : read_status === 2 ? (
              <Tooltip getPopupContainer={getPopupContainer} title={'已送达'}>
                <MyIcon className={style['read-status']} type='ecoboost-iconfontic_chat_send01' title={'已送达'} />
              </Tooltip>
            ) : null
          ) : null}
          {customExtra && customExtra.getMsgStatusType === 'delivered' ? (
            <Tooltip
              overlayClassName={style.pop_width}
              getPopupContainer={getPopupContainer}
              title={'已送达，此来源访客无法获取已读状态'}
            >
              <MyIcon
                className={style['read-status']}
                type='ecoboost-iconfontic_chat_send02'
                title={'已送达，此来源访客无法获取已读状态'}
              />
            </Tooltip>
          ) : null}
        </li>
        <li className={style['info-time']}>
          {moment
            .utc(time)
            .local()
            .format('MM-DD HH:mm:ss')}
        </li>
        {isAgent && agentName ? <li className={style['info-name']}>{agentName}</li> : null}
        {tag}
        {trusteeshipPermission && isTrusteeshipConv && extra && extra.unknown_type > -1 && convBelongToMe && (
          <TrusteeshipTag type={extra.unknown_type} />
        )}
      </ul>
      <div className={style['stream-content']}>
        {extra && extra.is_withdrawn && fromType != 'client' ? (
          <span className={style.withdraw}>{'已撤回'}</span>
        ) : null}
        {!isAgent && (
          <div className={style['content-wrap']} onMouseEnter={() => setHoverStatus(true)}>
            {render(data)}
          </div>
        )}

        {extra && extra.is_withdrawn && fromType === 'client' ? (
          <span className={style.withdraw}>{'已撤回'}</span>
        ) : null}

        {showOperate && (
          <ul className={style['operate-box']}>
            {canRemind && !extra?.is_withdrawn && (
              <Tooltip title={'回复'}>
                <li title={'回复'} onClick={() => handleRemindMessage(id)}>
                  <MessageOutlined />
                </li>
              </Tooltip>
            )}

            {canWithDraw ? (
              <Tooltip title={'撤回'}>
                <li title={'撤回'} onClick={() => handleWithdrawMessage(id, is_truested)}>
                  <RollbackOutlined />
                </li>
              </Tooltip>
            ) : null}

            {translatable ? (
              <Tooltip title={'翻译'}>
                <li title={'翻译'} onClick={() => toTranslate(data)}>
                  <i className='anticon'>
                    <svg
                      t='1630478283063'
                      className='icon'
                      viewBox='0 0 1024 1024'
                      version='1.1'
                      xmlns='http://www.w3.org/2000/svg'
                      p-id='2156'
                      width='14'
                      height='14'
                    >
                      <path
                        d='M938.666667 981.333333c-17.066667 0-29.866667-8.533333-38.4-25.6l-59.733334-119.466666h-277.333333l-59.733333 119.466666c-8.533333 21.333333-34.133333 29.866667-55.466667 17.066667-25.6-8.533333-34.133333-34.133333-21.333333-51.2l72.533333-140.8 145.066667-290.133333c12.8-21.333333 34.133333-38.4 59.733333-38.4s46.933333 12.8 59.733333 38.4l145.066667 290.133333 72.533333 140.8c8.533333 21.333333 0 46.933333-17.066666 55.466667-12.8 4.266667-17.066667 4.266667-25.6 4.266666z m-332.8-226.133333h192l-98.133334-192-93.866666 192zM85.333333 844.8c-17.066667 0-29.866667-8.533333-38.4-25.6-8.533333-21.333333 0-46.933333 21.333334-55.466667 93.866667-46.933333 179.2-110.933333 247.466666-187.733333-46.933333-64-85.333333-128-110.933333-192-8.533333-21.333333 4.266667-46.933333 25.6-55.466667 21.333333-8.533333 46.933333 4.266667 55.466667 25.6 21.333333 51.2 46.933333 102.4 81.066666 149.333334 59.733333-85.333333 102.4-179.2 128-281.6H85.333333c-25.6 0-42.666667-17.066667-42.666666-42.666667s17.066667-42.666667 42.666666-42.666667h243.2V85.333333c0-25.6 17.066667-42.666667 42.666667-42.666666s42.666667 17.066667 42.666667 42.666666v51.2h238.933333c25.6 0 42.666667 17.066667 42.666667 42.666667s-17.066667 42.666667-42.666667 42.666667h-68.266667c-25.6 128-85.333333 247.466667-162.133333 349.866666l25.6 25.6c17.066667 17.066667 17.066667 42.666667 0 59.733334-17.066667 17.066667-42.666667 17.066667-59.733333 0l-17.066667-17.066667c-72.533333 81.066667-162.133333 149.333333-264.533333 200.533333-8.533333 0-17.066667 4.266667-21.333334 4.266667z'
                        p-id='2157'
                        fill='#343b66'
                      ></path>
                    </svg>
                  </i>
                </li>
              </Tooltip>
            ) : null}

            {msgIntelligentrecommendAvailable ? (
              <Tooltip title={'智能推荐'}>
                <li title={'智能推荐'} onClick={() => toIntelligentRecommend(data)}>
                  <i className='anticon'>
                    <svg
                      t='1699926641438'
                      className='icon'
                      viewBox='0 0 1024 1024'
                      version='1.1'
                      xmlns='http://www.w3.org/2000/svg'
                      p-id='22618'
                      width='14'
                      height='14'
                    >
                      <path
                        d='M457.984 739.669333a36.864 36.864 0 0 1-68.010667 0l-34.218666-78.506666a301.994667 301.994667 0 0 0-153.770667-155.648l-94.208-41.813334a38.656 38.656 0 0 1 0-70.144l91.306667-40.533333A302.165333 302.165333 0 0 0 354.986667 192.170667l34.730666-83.626667a36.864 36.864 0 0 1 68.522667 0l34.730667 83.626667a302.165333 302.165333 0 0 0 155.904 160.853333l91.306666 40.533333a38.656 38.656 0 0 1 0 70.144l-94.293333 41.813334A301.994667 301.994667 0 0 0 492.202667 661.333333l-34.218667 78.421334zM221.013333 428.629333c89.173333 39.594667 161.706667 104.618667 202.922667 194.133334 41.301333-89.514667 113.834667-154.538667 202.922667-194.133334-90.197333-40.106667-162.986667-108.288-202.922667-199.338666C384 320.341333 311.296 388.608 221.013333 428.629333z m579.669334 501.930667l9.642666-22.101333a170.24 170.24 0 0 1 86.613334-87.808l29.696-13.141334a20.650667 20.650667 0 0 0 0-37.546666l-27.989334-12.458667A170.410667 170.410667 0 0 1 810.666667 666.709333l-9.898667-23.808a19.797333 19.797333 0 0 0-36.693333 0l-9.984 23.893334a170.325333 170.325333 0 0 1-87.893334 90.709333l-27.989333 12.458667a20.650667 20.650667 0 0 0 0 37.546666l29.696 13.141334c38.570667 17.152 69.461333 48.469333 86.613333 87.893333l9.642667 22.016c7.082667 16.128 29.44 16.128 36.437333 0z m-40.106667-142.08l22.016-21.845333 21.504 21.845333-21.504 21.162667-21.930667-21.162667z'
                        p-id='22619'
                        fill='#343b66'
                      ></path>
                    </svg>
                  </i>
                </li>
              </Tooltip>
            ) : null}

            {!speechToTxtAvailable ? (
              <Tooltip title={'当前版本不包含语音转文字功能'}>
                <li title={'语音转文字'} className={style.speech_disable}>
                  <MyIcon className={style.speech_icon} type='ecoboost-iconfonticon_chatvoice' />
                </li>
              </Tooltip>
            ) : speechToTxtAble ? (
              <Tooltip title={'语音转文字'}>
                <li title={'语音转文字'} onClick={() => toSpeechToTxt(data)}>
                  <MyIcon className={style.speech_icon} type='ecoboost-iconfonticon_chatvoice' />
                </li>
              </Tooltip>
            ) : null}
          </ul>
        )}

        {isAgent && (
          <div className={style['content-wrap']} onMouseEnter={() => setHoverStatus(true)}>
            {render(data)}
          </div>
        )}
      </div>
      {translate && <Translation translateData={translate} isAgent={isAgent}></Translation>}
      {speechToTxt && speechToTxt.fail && <div className={style['extreme-small']}>{'语音转文字失败'}</div>}
      {containsSensitiveWords ? (
        <SensitiveInfo />
      ) : failStatus ? (
        <FailSend
          type={content[0]?.body?.type}
          errorMsg={content[0]?.body?.errorMsg}
          clickFailIcon={() => clickFailIcon(content)}
        />
      ) : null}
    </div>
  );
});

export default MessageInfo;
